import React, { useState, useEffect, useContext, forwardRef, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Snackbar, Checkbox, Switch, CircularProgress } from "@mui/material";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Clear from "@mui/icons-material/Clear";
import { DeleteDialog } from "../components/DeleteDialog";
import { Link } from "react-router-dom";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import { where, or, query as firestoreQuery, getDocs, getDoc, doc } from "firebase/firestore";
import { Context } from "context/Wrapper";
import MuiAlert from "@mui/material/Alert";
import JobStatusReport from "components/JobStatusReport";
import {
  useAdvancedDataListener,
} from "apis/dataSnapshots";
import {
  collection,
  query,
  setDoc,
  updateDoc,
  writeBatch
} from "firebase/firestore";
import { db } from "config/config";
import AdvancedTable from "components/AdvancedTable";
import useCustomParams from "components/shared/useCustomParams";
import tableConfig from "config/advancedTableColumnsControl.json";
import { useSubscriptionState } from "context/SubscriptionContext";

const searchLimit = tableConfig?.searchMinLength || 3;

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const fetchDocumentsByIds = async (collectionName, ids, cacheRef) => {
  if (!ids || ids.length === 0) {
    console.log(`No ${collectionName} IDs to fetch`);
    return new Map();
  }

  const uniqueIds = [...new Set(ids.filter(id => id && id !== "undefined" && id !== ""))];
  if (uniqueIds.length === 0) {
    console.log(`No valid ${collectionName} IDs to fetch`);
    return new Map();
  }

  console.time(`fetch-${collectionName}-batch-${uniqueIds.length}`);

  const results = new Map();
  const uncachedIds = [];

  for (const id of uniqueIds) {
    if (cacheRef.current.has(id)) {
      results.set(id, cacheRef.current.get(id));
    } else {
      uncachedIds.push(id);
    }
  }

  console.log(`Fetching ${uncachedIds.length} uncached ${collectionName} out of ${uniqueIds.length} total`);

  if (uncachedIds.length === 0) {
    console.timeEnd(`fetch-${collectionName}-batch-${uniqueIds.length}`);
    return results;
  }

  const batchSize = 10;
  const batches = [];

  for (let i = 0; i < uncachedIds.length; i += batchSize) {
    batches.push(uncachedIds.slice(i, i + batchSize));
  }

  console.log(`Fetching ${uncachedIds.length} ${collectionName} in ${batches.length} batches`);

  try {
    for (const batchIds of batches) {
      try {
        const q = query(collection(db, collectionName), where("__name__", "in", batchIds));
        const snapshot = await getDocs(q);

        snapshot.forEach(doc => {
          const data = { id: doc.id, ...doc.data() };
          results.set(doc.id, data);
          cacheRef.current.set(doc.id, data);
        });

        console.log(`Fetched ${snapshot.size} ${collectionName} in batch`);
      } catch (error) {
        console.error(`Error fetching ${collectionName} batch:`, error);
        for (const id of batchIds) {
          try {
            const docRef = doc(db, collectionName, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
              const data = { id: docSnap.id, ...docSnap.data() };
              results.set(docSnap.id, data);
              cacheRef.current.set(docSnap.id, data);
            }
          } catch (individualError) {
            console.error(`Error fetching individual ${collectionName} ${id}:`, individualError);
          }
        }
      }
    }

    console.log(`Total ${collectionName} fetched: ${results.size} (${uncachedIds.length} were uncached)`);
  } catch (error) {
    console.error(`Error fetching ${collectionName} batches:`, error);
  }

  console.timeEnd(`fetch-${collectionName}-batch-${uniqueIds.length}`);
  return results;
};

export const Projects = (props) => {
  const projectLocationState = useLocation();
  const currentSubscription = useSubscriptionState();

  const [feedbackMessage, setFeedbackMessage] = useState({
    open: projectLocationState.state?.feedback?.message ? true : false,
    message: projectLocationState.state?.feedback ? projectLocationState.state?.feedback?.message : '',
    severity: projectLocationState.state?.feedback ? projectLocationState.state?.feedback?.severity : 'success',
  });

  const context = useContext(Context);
  const { updateParams, params } = useCustomParams();

  const { groupId, isPro, userId } = context?.state?.userProfile || {};

  const [reportOpen, setReportOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadingRelatedData, setLoadingRelatedData] = useState(false);
  const [isRelatedDataLoaded, setIsRelatedDataLoaded] = useState(false); // 🟢 NEW

  console.time('Projects component mount');

  const openReport = () => {
    setReportOpen(true);
  };
  const closeReport = () => {
    setReportOpen(false);
  };

  const user = context?.state?.userProfile || {};
  const { showAllDataFlag } = context?.state || {};

  const [flagged, setFlagged] = useState(false);

  const dateUpdatedColumn = {
    field: "dateUpdated",
    headerName: "Last Updated",
    renderCell: ({ row }) => {
      if (!row.dateUpdated) return "";
      const d = typeof row.dateUpdated === "number"
        ? new Date(row.dateUpdated)
        : row.dateUpdated.toDate();
      return d.toISOString().split("T")[0];
    },
    sortComparator: (_, __, c1, c2) => {
      const a = c1.row.dateUpdated, b = c2.row.dateUpdated;
      const ms1 = typeof a === "number" ? a : a?.toMillis?.() || 0;
      const ms2 = typeof b === "number" ? b : b?.toMillis?.() || 0;
      return ms1 - ms2;
    },
  };

  const [projects, setProjects] = useState([]);
  const [searchIndex, setSearchIndex] = useState([]);
  const [isSearchIndexLoaded, setIsSearchIndexLoaded] = useState(false);
  const [isSearchIndexEnriched, setIsSearchIndexEnriched] = useState(false); // 🟢 NEW
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState({
    itemId: "",
    identifier: "",
  });
  const sortField = params.get("sort") || "";
  const sortOrder = params.get("order") || "asc";
  const [searchValue, setSearchValue] = useState("");

  const buildingCacheRef = useRef(new Map());
  const contactCacheRef = useRef(new Map());
  const profileCacheRef = useRef(new Map());

  useEffect(() => {
    const gridFilter = params.get("search") || "";
    setSearchValue(gridFilter);
  }, [params.get("search")]);

  console.time('useAdvancedDataListener');
  const { data, count, refetch } = useAdvancedDataListener({
    user,
    condition: groupId && isPro && showAllDataFlag,
    collectionName: "projects",
    fromBuildingReferencesJobDetails: props.fromBuildingReferencesJobDetails,
    flagged,
    defaultSortField: "dateUpdated",
    defaultSortOrder: "desc",
  });
  console.timeEnd('useAdvancedDataListener');

  useEffect(() => {
    setProjects([]);
    setSearchResults([]);
    setSearchValue("");
    updateParams({ page: "", search: "", pageIncreasing: "" });

    refetch();
  }, [showAllDataFlag]);

  const fetchRelatedDataForProjects = useCallback(async (projectsData) => {
    if (!projectsData || projectsData.length === 0) return;

    setLoadingRelatedData(true);
    console.time('fetchRelatedDataForProjects');

    try {
      const contactIds = new Set();
      const buildingIds = new Set();
      const profileIds = new Set();

      projectsData.forEach(project => {
        if (project.client && project.client !== "undefined") contactIds.add(project.client);
        if (project.tenant && project.tenant !== "undefined") contactIds.add(project.tenant);
        if (project.applicant && project.applicant !== "undefined") contactIds.add(project.applicant);
        if (project.filingRep && project.filingRep !== "undefined") contactIds.add(project.filingRep);
        if (project.projectManager && project.projectManager !== "undefined") contactIds.add(project.projectManager);
        if (project.building && project.building !== "undefined") buildingIds.add(project.building);
        if (project.assignedTo && project.assignedTo !== "undefined") profileIds.add(project.assignedTo);
      });

      const fetchPromises = [];

      if (contactIds.size > 0) {
        fetchPromises.push(fetchDocumentsByIds("contacts", Array.from(contactIds), contactCacheRef));
      } else {
        fetchPromises.push(Promise.resolve(new Map()));
      }

      if (buildingIds.size > 0) {
        fetchPromises.push(fetchDocumentsByIds("buildings", Array.from(buildingIds), buildingCacheRef));
      } else {
        fetchPromises.push(Promise.resolve(new Map()));
      }

      if (profileIds.size > 0) {
        fetchPromises.push(fetchDocumentsByIds("profiles", Array.from(profileIds), profileCacheRef));
      } else {
        fetchPromises.push(Promise.resolve(new Map()));
      }

      const [contacts, buildings, profiles] = await Promise.all(fetchPromises);
      console.log(`Fetched ${contacts.size} contacts, ${buildings.size} buildings, ${profiles.size} profiles`);

      setIsRelatedDataLoaded(true);

    } catch (error) {
      console.error("Error fetching related data:", error);
    } finally {
      setIsRelatedDataLoaded(true);
      setLoadingRelatedData(false);
      console.timeEnd('fetchRelatedDataForProjects');
    }
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;

    setIsRelatedDataLoaded(false);

    fetchRelatedDataForProjects(data);
  }, [data, fetchRelatedDataForProjects]);

  const loadSearchIndex = useCallback(async () => {
    if (isSearchIndexLoaded && searchIndex.length > 0) return;
    console.time('loadSearchIndex');
    setIsSearching(true);

    try {
      const projectsRef = collection(db, "projects");
      let conditions = [];

      if (showAllDataFlag && groupId && userId) {
        conditions.push(
          or(
            where("groupId", "==", groupId),
            where("userId", "==", userId)
          )
        );
      } else if (userId) {
        conditions.push(where("userId", "==", userId));
      }

      if (flagged) {
        conditions.push(where("flagged", "==", true));
      }

      const q = firestoreQuery(projectsRef, ...conditions);
      const snapshot = await getDocs(q);

      const index = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          customerReference: data.customerReference || "",
          customerReference_lc: (data.customerReference || "").toLowerCase(),
          buildingString: "",
          buildingString_lc: "",
          buildingAddress: "",
          buildingAddress_lc: "",
          client_lc: "",
          tenant_lc: "",
          applicantString: "",
          status_lc: (data.status || "").toLowerCase(),
          aka_lc: (data.aka || "").toLowerCase(),
          DOBNumber: data.DOBNumber || "",
          filingRep_lc: "",
          assignedTo_lc: "",
          building: data.building,
          client: data.client,
          tenant: data.tenant,
          applicant: data.applicant,
          filingRep: data.filingRep,
          assignedTo: data.assignedTo,
          flagged: data.flagged || false
        };
      });

      setSearchIndex(index);
      setIsSearchIndexLoaded(true);
      console.log(`✅ Loaded ${index.length} projects into search index`);
      setIsSearchIndexEnriched(true);
    } catch (error) {
      console.error("Error loading search index:", error);
    } finally {
      setIsSearching(false);
      console.timeEnd('loadSearchIndex');
    }
  }, [groupId, userId, showAllDataFlag, flagged, isSearchIndexLoaded]);

  useEffect(() => {
    loadSearchIndex();
  }, [loadSearchIndex]);

  useEffect(() => {
    if (true) return;
    if (!isSearchIndexLoaded || searchIndex.length === 0) return;
    if (!isRelatedDataLoaded) return;
    if (contactCacheRef.current.size === 0 && buildingCacheRef.current.size === 0) return;

    console.time('enrichSearchIndex');

    const enrichedIndex = searchIndex.map(item => {
      const building = buildingCacheRef.current.get(item.building);
      const client = contactCacheRef.current.get(item.client);
      const tenant = contactCacheRef.current.get(item.tenant);
      const applicant = contactCacheRef.current.get(item.applicant);
      const filingRep = contactCacheRef.current.get(item.filingRep);
      const profile = profileCacheRef.current.get(item.assignedTo);
      const buildingAddress =
        (building?.customerReference || "").trim() ||
        `${building?.houseNumber || ""} ${building?.streetName || ""}`.trim();

      return {
        ...item,
        buildingString: building?.customerReference || "",
        buildingString_lc: (building?.customerReference || "").toLowerCase(),
        buildingAddress,
        buildingAddress_lc: buildingAddress.toLowerCase(),
        client_lc: (client?.businessName || "").toLowerCase(),
        tenant_lc: (tenant?.businessName || "").toLowerCase(),
        applicantString: applicant?.fullName || "",
        filingRep_lc: (filingRep?.businessName || filingRep?.fullName || "").toLowerCase(),
        assignedTo_lc: (profile?.name || "").toLowerCase(),
      };
    });

    setSearchIndex(enrichedIndex);
    setIsSearchIndexEnriched(true);
    console.timeEnd('enrichSearchIndex');
  }, [isSearchIndexLoaded, searchIndex.length, isRelatedDataLoaded, contactCacheRef.current.size, buildingCacheRef.current.size]);

  useEffect(() => {
    if (searchValue.length >= searchLimit && isSearchIndexEnriched) {
      performSearch(searchValue);
    }
  }, [isSearchIndexEnriched]);

  const performSearch = useCallback(
    (searchTerm) => {
      if (!searchTerm || searchTerm.length < searchLimit || searchIndex.length === 0) {
        setSearchResults([]);
        return [];
      }

      const q = searchTerm.toLowerCase().trim();

      const results = [];
      for (const item of searchIndex) {
        if (item.customerReference_lc?.includes(q)) {
          results.push(item);
        }
        if (results.length >= 100) break;
      }

      setSearchResults(results);
      return results;
    },
    [searchIndex, searchLimit]
  );


  useEffect(() => {
    if (searchValue.length >= searchLimit && isSearchIndexEnriched) {
      performSearch(searchValue);
    } else {
      setSearchResults([]);
    }
  }, [searchValue, performSearch, isSearchIndexEnriched]);

  useEffect(() => {
    updateParams({
      page: "",
      search: "",
      pageIncreasing: "",
    });
  }, []);

  useEffect(() => {
    if (data.length > 0 && isRelatedDataLoaded) {
      console.time('Map project data');
      const newprojects = data.map((project) => {
        const client = contactCacheRef.current.get(project.client);
        const tenant = contactCacheRef.current.get(project.tenant);
        const building = buildingCacheRef.current.get(project.building);
        const buildingAddress =
          (building?.customerReference || "").trim() ||
          `${building?.houseNumber || ""} ${building?.streetName || ""}`.trim();
        const filingRep = contactCacheRef.current.get(project.filingRep);
        const projectManager = contactCacheRef.current.get(project.projectManager);
        const applicant = contactCacheRef.current.get(project.applicant);
        const profile = profileCacheRef.current.get(project.assignedTo);

        return {
          ...project,
          clientString: client?.businessName || "",
          tenantString: tenant?.businessName || "",
          applicantString: applicant?.fullName || "",
          buildingString: building?.customerReference || "",
          applicant: project.applicant || "",
          buildingAddress,
          projectManagerName: projectManager?.businessName || projectManager?.fullName || "",
          filingRepName: filingRep?.businessName || filingRep?.fullName || "",
          assignedTo: profile?.name,
          buildingAddress_lc: buildingAddress.toLowerCase(),
          customerReference_lc: project.customerReference?.toLowerCase() || "",
          status_lc: project.status?.toLowerCase() || "",
          aka_lc: project.aka?.toLowerCase() || "",
          client_lc: client?.businessName?.toLowerCase() || "",
          tenant_lc: tenant?.businessName?.toLowerCase() || "",
          filingRep_lc: filingRep?.businessName?.toLowerCase() || filingRep?.fullName?.toLowerCase() || "",
          assignedTo_lc: profile?.name?.toLowerCase() || "",
          buildingString_lc: building?.customerReference?.toLowerCase() || "",
        };
      });

      setProjects(newprojects);
      if (isInitialLoad) {
        setIsInitialLoad(false);
        console.timeEnd('Projects component mount');
      }
      console.timeEnd('Map project data');
    }
  }, [data, isRelatedDataLoaded]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (!value || value.length < searchLimit) {
      updateParams({ search: "" });
    } else {
      updateParams({ search: value });
    }
  };

  const handleClearSearch = () => {
    setSearchValue("");
    updateParams({ search: "" });
    setSearchResults([]);
  };

  const HighlightText = ({ text = '', searchValue = '' }) => {
    if (!text || !searchValue || searchValue.length < searchLimit) {
      return <span>{text}</span>;
    }

    try {
      const parts = text.split(new RegExp(`(${escapeRegExp(searchValue)})`, 'gi'));
      return (
        <span>
          {parts.map((part, i) =>
            part.toLowerCase() === searchValue.toLowerCase() ? (
              <mark key={i} style={{ background: '#2769AA', color: 'white' }}>
                {part}
              </mark>
            ) : (
              <React.Fragment key={i}>{part}</React.Fragment>
            )
          )}
        </span>
      );
    } catch (error) {
      return <span>{text}</span>;
    }
  };

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const handleDelete = (id, identifier) => {
    setItemToDelete({ itemId: id, identifier: identifier });
    setDeleteOpen(true);
  };

  async function removeTodoCard(jobId, groupId) {
    const todosQ = query(
      collection(db, "todos"),
      where("groupId", "==", groupId)
    );
    const todosSnap = await getDocs(todosQ);
    if (todosSnap.empty) return;

    await Promise.all(
      todosSnap.docs.map(async todosDoc => {
        const board = todosDoc.data().board;
        const newBoard = {
          ...board,
          columns: board.columns.map(col => ({
            ...col,
            cards: col.cards.filter(c => c.id !== jobId),
          })),
        };
        await updateDoc(doc(db, "todos", todosDoc.id), { board: newBoard });
      })
    );
  }

  const confirmDelete = async () => {
    const projectId = itemToDelete.itemId;

    try {
      const batch = writeBatch(db);
      const projectRef = doc(db, "projects", projectId);
      batch.delete(projectRef);

      const workTypesSnap = await getDocs(
        query(collection(db, "workTypes"), where("projectId", "==", projectId))
      );
      workTypesSnap.forEach((d) => batch.delete(doc(db, "workTypes", d.id)));

      const filingsSnap = await getDocs(
        query(collection(db, "filings"), where("projectId", "==", projectId))
      );
      filingsSnap.forEach((d) => batch.delete(doc(db, "filings", d.id)));

      await batch.commit();

      await removeTodoCard(projectId, groupId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      setSearchIndex(prev => prev.filter(p => p.id !== projectId));

      openFeedbackMessage("Project deleted successfully!", "success");
      setDeleteOpen(false);
      navigate("/projects");
    } catch (error) {
      console.error("Error deleting project tasks:", error);
      openFeedbackMessage("Failed to delete project. Please try again.", "error");
    }
  };

  // 🟢 MAP SEARCH RESULTS TO FULL PROJECT DATA
  const searchCheck = searchValue.length >= searchLimit;

  const searchResultProjects = useMemo(() => {
    if (!searchCheck || searchResults.length === 0) return [];
    const resultIds = new Set(searchResults.map(r => r.id));
    return projects.filter(p => resultIds.has(p.id));
  }, [searchResults, projects, searchCheck]);

  // 🟢 SHOW LOADING STATE UNTIL EVERYTHING IS READY
  const isLoading = !isRelatedDataLoaded || !isSearchIndexLoaded || loadingRelatedData || isSearching;

  let projectCollection = props.fromBuildingReferencesJobDetails?.fromBuildingReferencesJob ?
    (searchCheck ? searchResultProjects.filter((item) => item.building == props.fromBuildingReferencesJobDetails.buildingId)
      : projects.filter((item) => item.building == props.fromBuildingReferencesJobDetails.buildingId))
    : (searchCheck ? searchResultProjects : projects);

  const openFeedbackMessage = (message, severity) => {
    setFeedbackMessage({ open: true, message, severity });
  };

  const handleCloseFeedbackMessage = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackMessage({ ...feedbackMessage, open: false });
  };

  const handleFlagged = () => {
    setFlagged((prev) => !prev);
    setIsSearchIndexLoaded(false);
    setIsSearchIndexEnriched(false);
    setSearchIndex([]);
  };

  console.log(`Projects: ${projects.length}, Related data loaded: ${isRelatedDataLoaded}, Search index: ${searchIndex.length}, Enriched: ${isSearchIndexEnriched}`);

  return (
    <>
      <Fade in={true}>
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={8} style={{ position: "sticky" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography align="left" variant="h4" sx={{ marginRight: "20px" }}>
                  JOBS
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    backgroundColor: "#e9e9e9",
                    borderRadius: "4px",
                    padding: "5px 17px",
                  }}
                >
                  <Typography variant="body1">Flagged Jobs</Typography>
                  <Switch
                    checked={flagged}
                    onChange={handleFlagged}
                    color="primary"
                    size="medium"
                  />
                </Box>
                {isLoading && (
                  <CircularProgress size={24} sx={{ ml: 2 }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Paper
                component="form"
                onSubmit={(e) => e.preventDefault()}
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  marginLeft: "auto",
                  position: 'relative',
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder={!isSearchIndexEnriched ? "Loading search..." : "Search"}
                  value={searchValue}
                  inputProps={{ "aria-label": "search" }}
                  onChange={handleSearch}
                  disabled={!isSearchIndexEnriched}
                />
                {searchCheck ? (
                  <IconButton
                    sx={{ p: "10px" }}
                    aria-label="clear search"
                    onClick={handleClearSearch}
                  >
                    <Clear />
                  </IconButton>
                ) : (
                  <IconButton sx={{ p: "10px" }} aria-label="search">
                    <SearchIcon />
                  </IconButton>
                )}
              </Paper>
            </Grid>
          </Grid>

          <AdvancedTable
            fromBuildingReferencesJobDetails={props.fromBuildingReferencesJobDetails}
            data={projectCollection}
            collectionName="projects"
            columnsRaw={[
              {
                field: "customerReference_lc",
                headerName: "Job ID",
                renderCell: ({ row }) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {row.flagged && (
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          backgroundColor: "red",
                          borderRadius: "50%",
                          marginRight: "8px",
                        }}
                      />
                    )}
                    <HighlightText
                      text={row.customerReference}
                      searchValue={searchValue}
                    />
                  </div>
                ),
              },
              {
                field: "buildingString_lc",
                headerName: "Building",
                renderCell: ({ row }) => (
                  <HighlightText text={row.buildingString} searchValue={searchValue} />
                ),
              },
              {
                field: "buildingAddress_lc",
                headerName: "Address",
                sortable: false,
                renderCell: ({ row }) => (
                  <HighlightText text={row.buildingAddress} searchValue={searchValue} />
                ),
              },
              {
                field: "status_lc",
                headerName: "Status",
                renderCell: ({ row }) => (
                  <HighlightText text={row.status} searchValue={searchValue} />
                ),
              },
              {
                field: "aka_lc",
                headerName: "AKA",
                renderCell: ({ row }) => (
                  <HighlightText text={row.aka} searchValue={searchValue} />
                ),
              },
              {
                field: "floors",
                headerName: "Floor(s)",
              },
              {
                field: "client_lc",
                headerName: "Client",
                renderCell: ({ row }) => (
                  <HighlightText text={row.clientString} searchValue={searchValue} />
                ),
              },
              {
                field: "tenant_lc",
                headerName: "Tenant",
                renderCell: ({ row }) => (
                  <HighlightText text={row.tenantString} searchValue={searchValue} />
                ),
              },
              {
                field: "DOBNumber",
                headerName: "DOB Number",
                renderCell: ({ row }) => (
                  <HighlightText
                    text={row.DOBNumber?.toString()}
                    searchValue={searchValue}
                  />
                ),
              },
              {
                field: "applicant",
                headerName: "Applicant ID",
                renderCell: ({ row }) => row.applicant || "",
              },
              {
                field: "applicant_lc",
                headerName: "Applicant Name",
                renderCell: ({ row }) => (
                  <HighlightText text={row.applicantString} searchValue={searchValue} />
                ),
              },
              {
                field: "building",
                headerName: "Building ID",
              },
              {
                field: "client",
                headerName: "Client ID",
              },
              {
                field: "filingRep_lc",
                headerName: "Filing Rep",
                renderCell: ({ row }) => (
                  <HighlightText text={row.filingRepName} searchValue={searchValue} />
                ),
              },
              {
                field: "projectManagerName",
                headerName: "Project Manager",
              },
              ...(currentSubscription?.isPro
                ? [
                  {
                    field: "assignedTo_lc",
                    headerName: "Assigned To",
                    renderCell: ({ row }) => (
                      <HighlightText text={row.assignedTo} searchValue={searchValue} />
                    ),
                  },
                ]
                : []),
              {
                field: "flagged",
                headerName: "Flagged",
                renderCell: ({ row }) => (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: row.flagged ? "Yes" : "No",
                    }}
                  />
                ),
              },
              ...(tableConfig.buildings.dateUpdated
                ? [dateUpdatedColumn]
                : []),
            ]}
            rowCount={searchCheck ? searchResultProjects.length : count}
            handleDelete={handleDelete}
            isInactive={props.isInactive}
          />

          <Box
            sx={{
              position: "fixed",
              bottom: "1rem",
              "& > :not(style)": { m: 1 },
            }}
          >
            {!props.isInactive && (
              <Link to="/projects/new" state={{ fromBuildingReferencesJobDetails: props.fromBuildingReferencesJobDetails }}>
                <Fab
                  color="primary"
                  aria-label="add"
                  variant="extended"
                  disabled={props.isInactive}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  New
                </Fab>
              </Link>
            )}
            <Fab
              color="primary"
              aria-label="report"
              variant="extended"
              onClick={openReport}
              sx={{ ml: 2 }}
            >
              <DownloadForOfflineIcon sx={{ mr: 1 }} />
              Generate Job Status Report
            </Fab>
          </Box>
        </div>
      </Fade>
      <DeleteDialog
        open={deleteOpen}
        confirmDelete={confirmDelete}
        setDialogOpen={setDeleteOpen}
        identifier={itemToDelete.identifier}
      />
      <Snackbar
        open={feedbackMessage.open}
        autoHideDuration={2000}
        onClose={handleCloseFeedbackMessage}
      >
        <Alert
          onClose={handleCloseFeedbackMessage}
          severity={feedbackMessage.severity}
          sx={{ width: "100%" }}
        >
          {feedbackMessage.message}
        </Alert>
      </Snackbar>
      <JobStatusReport open={reportOpen} onClose={closeReport} />
    </>
  );
};